import React, { useMemo } from 'react';
import styled, { keyframes, css, createGlobalStyle } from 'styled-components';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import walkSong from './assets/walk-song.mp3';
import spots from './spots.json';
import { ResilientImg } from './ResilientImg';

// Fix default marker icon in bundler (CRA)
const createMarkerIcon = (index) =>
  L.divIcon({
    className: 'path-mini-marker',
    html: `<span class="path-marker-dot">${index + 1}</span>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });

function FitBounds({ points }) {
  const map = useMap();
  React.useEffect(() => {
    if (!points.length) return;
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 14);
    } else {
      const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
      if (bounds.isValid()) map.fitBounds(bounds, { padding: [20, 20], maxZoom: 14 });
    }
  }, [map, points]);
  return null;
}

function PathMiniMap({ points }) {
  const positions = useMemo(() => points.map((p) => [p.lat, p.lng]), [points]);
  const center = useMemo(() => {
    if (!points.length) return [25.05, 121.5];
    const lat = points.reduce((s, p) => s + p.lat, 0) / points.length;
    const lng = points.reduce((s, p) => s + p.lng, 0) / points.length;
    return [lat, lng];
  }, [points]);

  if (!points.length) return null;

  return (
    <MapContainer
      center={center}
      zoom={12}
      style={{ height: '100%', width: '100%', borderRadius: 8 }}
      scrollWheelZoom={false}
      zoomControl={false}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <FitBounds points={points} />
      <Polyline positions={positions} color="#345f9a" weight={3} opacity={0.8} />
      {points.map((p, i) => (
        <Marker key={i} position={[p.lat, p.lng]} icon={createMarkerIcon(i)}>
          {p.name && <Popup>{p.name}</Popup>}
        </Marker>
      ))}
    </MapContainer>
  );
}

const SPOT_BY_ID = Array.isArray(spots)
  ? spots.reduce((map, spot) => {
      if (spot && spot.id) {
        map[spot.id] = spot;
      }
      return map;
    }, {})
  : {};

const CardWrapper = styled.div`
  width: 320px;
  max-width: calc(100vw - 32px);
  box-sizing: border-box;
  @media (max-width: 480px) {
    width: min(320px, calc(100vw - 24px));
  }
  ${(props) =>
    props.$inline
      ? `
  position: relative;
  margin: 0 auto;
  pointer-events: auto;
  opacity: 1;
  transform: none;
  `
      : `
  position: absolute;
  left: 50%;
  top: 50%;
  transition: transform 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94), z-index 0s, opacity 0.3s ease;
  transform-origin: center center;
  z-index: ${(() => {
    const n = props.$totalCount || 0;
    if (props.$active) return 100 + n + 1;
    const activeIdx = props.$activeIdx || 0;
    const dist = Math.abs(props.$index - activeIdx);
    const maxDist = Math.max(activeIdx, n - 1 - activeIdx);
    return 100 + (maxDist - dist);
  })()};
  pointer-events: none;
  opacity: ${props.$active ? 1 : 0.92};
  transform: ${(() => {
    const base = 'translate(-50%, -50%)';
    const n = props.$totalCount || 1;
    const stackSpan = 70;
    const t = n <= 1 ? 0.5 : props.$index / Math.max(1, n - 1);
    const baseOffsetY = -stackSpan / 2 + t * stackSpan;
    if (props.$active) {
      return `${base} scale(1) translateY(${baseOffsetY}px) rotate(${props.$angle}deg)`;
    }
    const behind = props.$index - props.$activeIdx;
    const extraOffsetY =
      behind > 0 ? 18 + Math.min(behind * 6, 24) : 18 - Math.min(-behind * 6, 24);
    const scale = 1 - Math.min(Math.abs(behind) * 0.04, 0.2);
    return `${base} scale(${scale}) translateY(${
      baseOffsetY + (behind > 0 ? extraOffsetY : -extraOffsetY)
    }px) rotate(${props.$angle}deg)`;
  })()};
  `}
`;

/* Photo section：放在 path section 下方，卡片內；可含 vinyl + 照片 */
const PhotoSection = styled.div`
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(52, 95, 154, 0.06);
  border: 1px solid rgba(52, 95, 154, 0.14);
  pointer-events: auto;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
`;

const PHOTOS_PER_ROW = 3;

const PhotoGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: 0;
`;

const PhotoThumb = styled.button`
  padding: 0;
  border: none;
  border-radius: 8px;
  overflow: hidden;
  background: #e8e4dc;
  cursor: pointer;
  line-height: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  pointer-events: auto;

  & img {
    display: block;
    width: 72px;
    height: 72px;
    object-fit: cover;
    vertical-align: top;
  }
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
  }
`;

const PhotoMoreThumb = styled(PhotoThumb)`
  position: relative;
`;

const PhotoMoreOverlay = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.42);
  color: #fff;
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.35);
  pointer-events: none;
`;

const vinylSpin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const VinylInSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  flex-shrink: 0;
  pointer-events: auto;
`;

const VinylBox = styled.div`
  position: relative;
  width: 100px;
  height: 100px;
  flex-shrink: 0;
  @media (max-width: 480px) {
    width: 80px;
    height: 80px;
  }
`;

const VinylDisc = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: radial-gradient(
    circle at 50% 50%,
    #1a1a1a 0%,
    #0d0d0d 12%,
    #222 18%,
    #0d0d0d 22%,
    #1a1a1a 28%,
    #0d0d0d 35%,
    #252525 42%,
    #0d0d0d 50%,
    #1a1a1a 58%,
    #0d0d0d 65%,
    #222 72%,
    #0d0d0d 78%,
    #1a1a1a 88%,
    #0d0d0d 100%
  );
  box-shadow:
    inset 0 0 20px rgba(0, 0, 0, 0.8),
    inset 0 2px 4px rgba(255, 255, 255, 0.06),
    0 6px 20px rgba(0, 0, 0, 0.5);
  animation: ${(p) => (p.$isPlaying ? css`${vinylSpin} 2s linear infinite` : 'none')};
`;

const VinylCenter = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: radial-gradient(circle at 30% 30%, #3d3d3d, #1a1a1a);
  box-shadow: inset 0 2px 6px rgba(0, 0, 0, 0.6), 0 1px 0 rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  cursor: pointer;
  pointer-events: auto;
  transition: transform 0.15s ease;
  &:hover {
    transform: translate(-50%, -50%) scale(1.08);
  }
  @media (max-width: 480px) {
    width: 28px;
    height: 28px;
  }
`;

const Card = styled.div`
  position: relative;
  z-index: 10;
  flex: 1;
  min-width: 0;
  margin-left: 0;
  background: #fffdf8;
  border: 1px solid #f2e9dc;
  border-radius: 14px;
  padding: 14px 14px 18px;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.28);
  display: flex;
  flex-direction: column;
  gap: 12px;
  transition: box-shadow 0.2s ease;
  pointer-events: auto;

  &::after {
    content: '';
    position: absolute;
    top: 32px;
    left: 8px;
    right: 8px;
    height: 0;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
    box-shadow: 0 1px 0 rgba(255, 255, 255, 0.4);
  }

  &:hover {
    box-shadow: 0 14px 32px rgba(0, 0, 0, 0.32);
  }
`;

const CardHeader = styled.div`
  height: 32px;
  margin: -14px -14px 0 -14px;
  padding: 0 14px;
  display: flex;
  align-items: center;
  background: linear-gradient(180deg, #ebe4da 0%, #e3dcd1 50%, #ddd4c8 100%);
  border-radius: 14px 14px 0 0;
  border: 1px solid #f2e9dc;
  border-bottom: none;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.6), inset 0 -1px 0 rgba(0, 0, 0, 0.06);
`;

const CardBodyWrap = styled.div`
  padding: 6px 2px 0;
  color: #1f2430;
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: 0.95rem;
  font-weight: 800;
  letter-spacing: 0.01em;
  color: #3f3a33;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CardMetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 0.75rem;
  opacity: 0.75;
  min-height: 24px;
`;

const TagsRow = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
  & > * {
    flex-shrink: 0;
  }
`;

const Tag = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  background: #f3efe6;
  border: 1px solid #e6dece;
  border-radius: 999px;
  padding: 2px 6px;
  font-size: 0.7rem;
  color: #3f3a33;
  cursor: pointer;
  pointer-events: auto;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.08);
  }
`;

/* 共用折疊按鈕 */
const CollapseToggleBtn = styled.button`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  border: none;
  border-radius: 6px;
  background: rgba(52, 95, 154, 0.12);
  color: #345f9a;
  cursor: pointer;
  pointer-events: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  &:hover {
    background: rgba(52, 95, 154, 0.2);
  }
`;

const CollapsibleContentWrap = styled.div`
  position: relative;
  ${(p) =>
    p.$collapsed &&
    `
    max-height: 1.6em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `}
`;

const ContentSection = styled.div`
  position: relative;
  margin-top: 8px;
  padding-right: 28px;
`;

const CardBody = styled.p`
  margin: 0;
  line-height: 1.6;
  color: #2d2a32;
`;

const LinkList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 6px;
`;

const ExternalLink = styled.a`
  color: #345f9a;
  text-decoration: none;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  pointer-events: auto;

  &:hover {
    text-decoration: underline;
  }
`;

const LocationAnchor = styled.a`
  margin-left: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 999px;
  background: rgba(52, 95, 154, 0.08);
  color: #345f9a;
  text-decoration: none;
  font-size: 0.85rem;
  pointer-events: auto;
  transition: background-color 0.12s ease, transform 0.12s ease;

  &:hover {
    background: rgba(52, 95, 154, 0.16);
    transform: translateY(-1px);
  }
`;

const ItinerarySection = styled.div`
  position: relative;
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(52, 95, 154, 0.06);
  border: 1px solid rgba(52, 95, 154, 0.14);
`;

const ItineraryList = styled.div`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding-right: 28px;
`;

/* 整塊 path（地圖 + 行程列表）收合時顯示的一列 */
const PathSectionCollapsedRow = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  padding: 8px 10px;
  border: 1px solid rgba(52, 95, 154, 0.14);
  border-radius: 8px;
  background: rgba(52, 95, 154, 0.06);
  color: #345f9a;
  font-size: 0.8rem;
  cursor: pointer;
  pointer-events: auto;
  text-align: left;
  &:hover {
    background: rgba(52, 95, 154, 0.1);
  }
`;

const ItinerarySpotLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #345f9a;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.9rem;
  pointer-events: auto;
  padding: 4px 8px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.7);
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 1);
    text-decoration: ${(p) => (p.$hasUrl ? 'underline' : 'none')};
  }
  ${(p) => !p.$hasUrl && 'cursor: default; opacity: 0.85;'}
`;

const ItinerarySpotSpan = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #345f9a;
  font-weight: 600;
  font-size: 0.9rem;
  padding: 4px 8px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.55);
  opacity: 0.85;
`;

const PathMarkerGlobalStyle = createGlobalStyle`
  .path-mini-marker.leaflet-marker-icon {
    background: none !important;
    border: none !important;
  }
  .path-marker-dot {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #345f9a;
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    box-shadow: 0 1px 4px rgba(0,0,0,0.25);
  }
`;

const MiniMapWrap = styled.div`
  width: 100%;
  height: 120px;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 10px;
  border: 1px solid rgba(52, 95, 154, 0.12);
  background: #e8e6e1;
  pointer-events: auto;
  .leaflet-container {
    font-family: inherit;
  }
`;

export function MemoryCard({
  entry,
  idx,
  activeIdx,
  totalCount,
  angle,
  getEntryPhotos,
  getPhotoRotation,
  onOpenGallery,
  onTagClick,
  isVinylEntry,
  walkSongRef,
  vinylPlaying,
  setVinylPlaying,
  onWalkSongTimeUpdate,
  inline,
  onCardClick,
}) {
  const photos = getEntryPhotos(entry);
  const isActive = idx === activeIdx;
  const showVinyl = isActive && isVinylEntry;

  // Content has path (行程), each item: { spotId, note? } → Spot
  const pathItems =
    Array.isArray(entry.path) && entry.path.length > 0
      ? entry.path
          .map((it) => {
            const spot = it.spotId ? SPOT_BY_ID[it.spotId] : null;
            return spot ? { spot, note: it.note } : null;
          })
          .filter(Boolean)
      : [];

  const usePath = pathItems.length > 0;
  const [isPathSectionExpanded, setIsPathSectionExpanded] = React.useState(false);
  const [isContentExpanded, setIsContentExpanded] = React.useState(false);
  const [contentOverflowsOneLine, setContentOverflowsOneLine] = React.useState(false);
  const contentWrapRef = React.useRef(null);
  const hasContent = !!(entry.summary || entry.details);

  const allTags = useMemo(() => {
    const tags = [...(entry.tags || [])];
    (entry.path || []).forEach(p => (p.tags || []).forEach(t => { if (!tags.includes(t)) tags.push(t); }));
    return tags;
  }, [entry]);

  React.useLayoutEffect(() => {
    if (!hasContent) return;
    if (isContentExpanded) {
      setContentOverflowsOneLine(true);
      return;
    }
    const el = contentWrapRef.current;
    if (!el) return;
    // 收合時為 nowrap，內容是單行；用 scrollWidth > clientWidth 判斷是否有被裁切
    setContentOverflowsOneLine(el.scrollWidth > el.clientWidth);
  }, [hasContent, isContentExpanded, entry.summary, entry.details]);
  const pointsWithCoords = usePath
    ? pathItems
        .map(({ spot }) => spot)
        .filter((s) => s && typeof s.lat === 'number' && typeof s.lng === 'number')
        .map((s) => ({ lat: s.lat, lng: s.lng, name: s.name }))
    : [];
  const hasMap = pointsWithCoords.length > 0;
  // Show spot names in collapsed label (up to 2 spots, then "...+N")
  const pathSectionCollapsedLabel = (() => {
    if (!pathItems.length) return '📍 行程';
    const names = pathItems.map(({ spot, note }) => note ? `${note} · ${spot.name}` : spot.name);
    if (names.length <= 2) return `📍 ${names.join(' → ')}`;
    return `📍 ${names.slice(0, 2).join(' → ')} …+${names.length - 2}`;
  })();

  // Backward compat: single spot or links when no path
  const primarySpotId =
    entry.spotId ||
    (Array.isArray(entry.spotIds) && entry.spotIds.length > 0 ? entry.spotIds[0] : null);
  const primarySpot = primarySpotId ? SPOT_BY_ID[primarySpotId] : null;
  const primaryLocation =
    primarySpot && primarySpot.googleMapUrl
      ? { label: primarySpot.name || primarySpotId, url: primarySpot.googleMapUrl }
      : !usePath && entry.links && entry.links.length > 0
        ? entry.links[0]
        : null;
  const otherLinks =
    !usePath && entry.links && entry.links.length > 0 && primaryLocation
      ? entry.links.filter((link) => link.url !== primaryLocation.url)
      : entry.links || [];

  return (
    <CardWrapper
      $active={isActive}
      $index={idx}
      $activeIdx={activeIdx}
      $totalCount={totalCount}
      $angle={angle}
      $inline={inline}
      onClick={() => {
        if (typeof onCardClick === 'function') {
          onCardClick(entry, idx);
        }
      }}
    >
      <Card>
        <CardHeader>
          <CardTitle title={entry.title}>{entry.title}</CardTitle>
          {!usePath && primaryLocation?.url && (
            <LocationAnchor
              href={primaryLocation.url}
              target="_blank"
              rel="noopener noreferrer"
              title={primaryLocation.label || '打開地圖'}
              aria-label={primaryLocation.label || '打開地圖'}
              onClick={(e) => e.stopPropagation()}
              style={{ flexShrink: 0, marginLeft: 4 }}
            >
              📍
            </LocationAnchor>
          )}
        </CardHeader>
        <CardBodyWrap>
          <CardMetaRow>
            {entry.timestamp && <span>{entry.timestamp}</span>}
            {allTags.length > 0 && (
              <TagsRow>
                {allTags.map((tag) => (
                  <Tag
                    key={`${entry.id}-${tag}`}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onTagClick(tag);
                    }}
                  >
                    #{tag}
                  </Tag>
                ))}
              </TagsRow>
            )}
          </CardMetaRow>
          {hasContent && (
            <ContentSection>
              <CollapsibleContentWrap ref={contentWrapRef} $collapsed={!isContentExpanded}>
                {entry.summary && <CardBody>{entry.summary}</CardBody>}
                {entry.details && <CardBody style={{ opacity: 0.8 }}>{entry.details}</CardBody>}
              </CollapsibleContentWrap>
              {(isContentExpanded || contentOverflowsOneLine) && (
                <CollapseToggleBtn
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsContentExpanded((prev) => !prev);
                  }}
                  title={isContentExpanded ? '收合內容' : '展開內容'}
                  aria-label={isContentExpanded ? '收合內容' : '展開內容'}
                  style={{ position: 'absolute', top: 0, right: 0 }}
                >
                  {isContentExpanded ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                      <path d="M7 14l5-5 5 5z" />
                    </svg>
                  ) : (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                      <path d="M7 10l5 5 5-5z" />
                    </svg>
                  )}
                </CollapseToggleBtn>
              )}
            </ContentSection>
          )}
          {usePath && pathItems.length > 0 && (
            <ItinerarySection>
              <PathMarkerGlobalStyle />
              {!isPathSectionExpanded ? (
                <PathSectionCollapsedRow
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsPathSectionExpanded(true);
                  }}
                  title="展開地圖與行程"
                >
                  {pathSectionCollapsedLabel}
                </PathSectionCollapsedRow>
              ) : (
                <>
                  <CollapseToggleBtn
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsPathSectionExpanded(false);
                    }}
                    title="收合地圖與行程"
                    aria-label="收合地圖與行程"
                    style={{ position: 'absolute', top: 4, right: 4, zIndex: 500 }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                      <path d="M7 14l5-5 5 5z" />
                    </svg>
                  </CollapseToggleBtn>
                  {hasMap && (
                    <MiniMapWrap title="行程地點">
                      <PathMiniMap points={pointsWithCoords} />
                    </MiniMapWrap>
                  )}
                  <ItineraryList>
                    {pathItems.map(({ spot, note }, i) => {
                      const hasUrl = !!(spot.googleMapUrl && spot.googleMapUrl.trim());
                      const label = note ? `${note} · ${spot.name}` : spot.name;
                      return hasUrl ? (
                        <ItinerarySpotLink
                          key={`${entry.id}-${spot.id}-${i}`}
                          href={spot.googleMapUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          title="打開地圖"
                          $hasUrl
                        >
                          📍 {label}
                        </ItinerarySpotLink>
                      ) : (
                        <ItinerarySpotSpan key={`${entry.id}-${spot.id}-${i}`}>
                          📍 {label}
                        </ItinerarySpotSpan>
                      );
                    })}
                  </ItineraryList>
                </>
              )}
            </ItinerarySection>
          )}
          {(showVinyl || photos.length > 0) && (
            <PhotoSection>
              {showVinyl && (
                <VinylInSection onClick={(e) => e.stopPropagation()} title="天気いいから、散歩しましょう">
                  <audio
                    ref={walkSongRef}
                    src={walkSong}
                    onEnded={() => setVinylPlaying(false)}
                    onTimeUpdate={() => {
                      const t = walkSongRef?.current?.currentTime;
                      if (t != null && onWalkSongTimeUpdate) onWalkSongTimeUpdate(t);
                    }}
                  />
                  <VinylBox>
                    <VinylDisc $isPlaying={vinylPlaying} />
                    <VinylCenter
                      onClick={(e) => {
                        e.stopPropagation();
                        const el = walkSongRef?.current;
                        if (!el) return;
                        if (vinylPlaying) {
                          el.pause();
                        } else {
                          el.play().catch(() => {});
                        }
                        setVinylPlaying((p) => !p);
                      }}
                      aria-label={vinylPlaying ? '暫停' : '播放'}
                    >
                      {vinylPlaying ? (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                          <rect x="6" y="4" width="4" height="16" rx="1" />
                          <rect x="14" y="4" width="4" height="16" rx="1" />
                        </svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      )}
                    </VinylCenter>
                  </VinylBox>
                </VinylInSection>
              )}
              {photos.length > 0 && (
                <PhotoGrid>
                  {(photos.length > PHOTOS_PER_ROW
                    ? photos.slice(0, PHOTOS_PER_ROW - 1)
                    : photos.slice(0, PHOTOS_PER_ROW)
                  ).map((src, i) => (
                    <PhotoThumb
                      key={i}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenGallery(entry, i);
                      }}
                      title="點擊查看照片"
                    >
                      <ResilientImg
                        src={src}
                        alt=""
                        rotateDeg={
                          typeof getPhotoRotation === 'function'
                            ? getPhotoRotation(entry.id, i, src)
                            : undefined
                        }
                      />
                    </PhotoThumb>
                  ))}
                  {photos.length > PHOTOS_PER_ROW && (
                    <PhotoMoreThumb
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenGallery(entry, PHOTOS_PER_ROW - 1);
                      }}
                      title="點擊查看照片"
                    >
                      <ResilientImg
                        src={photos[PHOTOS_PER_ROW - 1]}
                        alt=""
                        rotateDeg={
                          typeof getPhotoRotation === 'function'
                            ? getPhotoRotation(
                                entry.id,
                                PHOTOS_PER_ROW - 1,
                                photos[PHOTOS_PER_ROW - 1]
                              )
                            : undefined
                        }
                      />
                      <PhotoMoreOverlay>共 {photos.length} 張</PhotoMoreOverlay>
                    </PhotoMoreThumb>
                  )}
                </PhotoGrid>
              )}
            </PhotoSection>
          )}
          {!usePath && otherLinks && otherLinks.length > 0 && (
            <LinkList>
              {otherLinks.map((link) => (
                <ExternalLink
                  key={`${entry.id}-${link.url}`}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  🔗 {link.label}
                </ExternalLink>
              ))}
            </LinkList>
          )}
        </CardBodyWrap>
      </Card>
    </CardWrapper>
  );
}
